/**
 * The two card faces every gendered-noun deck uses, in one place.
 *
 * Four decks ask two questions about the same kind of word:
 *
 *   Nouns          German      what does it mean?      GenderedTranslationFront
 *   Substantivos   Portuguese  what does it mean?      GenderedTranslationFront
 *   Der·Die·Das    German      which article?          ArticleChoiceFront
 *   Gêneros        Portuguese  which article?          ArticleChoiceFront
 *
 * They were four hand-written components until the Portuguese pair arrived and
 * made the duplication obvious — the second copy of a thing is where you find
 * out it was never language-specific. Nothing here knows which language it is
 * rendering; the article set is a parameter and the colours come from
 * `@/lib/gender`, which maps `der` and `o` to the same blue on purpose.
 *
 * The point is upkeep. Feedback on one of these decks becomes a fix in every
 * language at once, which is the whole reason a second language shares a
 * codebase instead of forking one.
 */
import { useEffect, useRef } from 'react';

import type { Card } from '@/core/types';
import { articleBorder, articleHover, articleText, type Article } from '@/lib/gender';
import { cn } from '@/lib/utils';

import type { AnswerEvent } from '@/apps/types';

import { TypeTranslationCardFront } from './TypeTranslationCard';

/**
 * The shape both languages' noun payloads share.
 *
 * Structural rather than a shared type, so `NounsPayload` keeps its
 * `der | die | das` and the Portuguese one keeps `o | a`. A union admitting
 * `das` for Portuguese would let through exactly the bug the types exist to
 * prevent.
 */
export type GenderedNoun = {
  article: Article;
  /** Null when the noun has no plural anybody uses — `o dinheiro`, `a fome`. */
  plural: string | null;
};

/**
 * Show the article, ask what the word means.
 *
 * The article is displayed rather than tested — its own deck does that — so a
 * learner meets the gender on every card without being graded on it twice.
 */
export function GenderedTranslationFront<T extends GenderedNoun>({
  card,
  onAnswer,
}: {
  card: Card<T>;
  onAnswer: (event: AnswerEvent) => void;
}) {
  const { article, plural } = card.payload;
  return (
    <TypeTranslationCardFront
      card={card}
      onAnswer={onAnswer}
      frameClassName={cn('border-l-8', articleBorder(article))}
      headword={
        <>
          <span className={articleText(article)}>{article} </span>
          {card.de}
        </>
      }
      subtitle={plural || null}
    />
  );
}

/**
 * Show the word, ask for its article.
 *
 * `articles` is a parameter because that is the only real difference between
 * the German and Portuguese versions: three buttons or two. Portuguese does not
 * have a smaller version of a German idea here — it simply never produces a
 * third gender, which is the first thing German makes you carry and Portuguese
 * takes away.
 */
export function ArticleChoiceFront<T extends { article: Article }>({
  card,
  onAnswer,
  articles,
}: {
  card: Card<T>;
  onAnswer: (event: AnswerEvent) => void;
  articles: Article[];
}) {
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
  }, [card.id]);

  const submit = (chosen: Article | null) => {
    onAnswer({
      correct: chosen === card.payload.article,
      user_answer: chosen,
      time_ms: Date.now() - startRef.current,
    });
  };

  return (
    <div className="border-border bg-card flex flex-col items-center gap-8 rounded-2xl border p-8 shadow-sm sm:p-10">
      <div className="font-display text-5xl font-semibold sm:text-6xl">{card.de}</div>

      <div
        className={cn('grid w-full gap-3', articles.length === 2 ? 'grid-cols-2' : 'grid-cols-3')}
      >
        {articles.map((article) => (
          <button
            key={article}
            type="button"
            onClick={() => submit(article)}
            className={cn(
              'border-border bg-card focus-visible:ring-ring min-h-14 rounded-lg border-2 text-lg font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              articleHover(article),
            )}
          >
            {article}
          </button>
        ))}
      </div>

      {/* Not optional. A card the user cannot answer has two outcomes, an
          honest blank or a guess, and a guess writes a wrong answer into
          `attempts.user_answer` that the mistakes list then reports as a
          misconception the user never held. */}
      <button
        type="button"
        onClick={() => submit(null)}
        className="text-muted-foreground text-sm underline-offset-4 hover:underline"
      >
        I don&apos;t know
      </button>
    </div>
  );
}
